/**
 * Adventskalender bundle builder
 * ------------------------------
 * State lives in one custom element: the picked variants, how many slots are open,
 * and everything derived from them (slot rows, prices, card states, CTA). Nothing
 * is written to the cart until the final add to cart, which posts every line in a
 * single /cart/add.js request and re-uses the theme's own cart events so the
 * drawer behaves exactly like it does elsewhere.
 *
 * The section only renders the open tab. Every other subcollection is fetched
 * through the Section Rendering API (sections/advent-bundle-cards.liquid) the
 * first time its tab is clicked, then kept in the grid for the session.
 *
 * Pricing: included products are charged in full and form the starting price.
 * Picked products are previewed with the section's percentage off, rounded per
 * line the same way Shopify rounds a percentage discount.
 */
(() => {
  const formatWithDelimiters = (cents, precision = 2, thousands = ',', decimal = '.') => {
    if (isNaN(cents) || cents == null) return '0';

    const parts = (cents / 100.0).toFixed(precision).split('.');
    const integer = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);

    return integer + (parts[1] ? decimal + parts[1] : '');
  };

  const formatMoney = (cents) => {
    const format = window.themeVariables?.settings?.moneyFormat || '{{amount}}';
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const name = format.match(placeholder)?.[1] || 'amount';
    let value;

    switch (name) {
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_with_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ', ',');
        break;
      case 'amount_with_period_and_space_separator':
        value = formatWithDelimiters(cents, 2, ' ', '.');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'", '.');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }

    return format.replace(placeholder, value);
  };

  const escapeHtml = (value = '') =>
    String(value).replace(/[&<>"']/g, (char) => {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char];
    });

  class AdventBundle extends HTMLElement {
    connectedCallback() {
      this.bundleSize = parseInt(this.dataset.bundleSize, 10) || 3;
      this.bundleMax = Math.max(parseInt(this.dataset.bundleMax, 10) || this.bundleSize, this.bundleSize);
      this.slotCount = this.bundleSize;
      this.bundleName = this.dataset.bundleName || 'Bundle';
      this.discount = parseInt(this.dataset.discount, 10) || 0;
      this.allowDuplicates = this.dataset.allowDuplicates === 'true';

      this.strings = {
        remaining: this.dataset.stringRemaining || 'Vælg [mangler] mere',
        cta: this.dataset.stringCta || 'Læg i kurven',
        add: this.dataset.stringAdd || 'Tilføj',
        added: this.dataset.stringAdded || 'Tilføjet',
        full: this.dataset.stringFull || 'Kalender fuld',
        chooseSize: this.dataset.stringChooseSize || 'Vælg størrelse',
        selected: this.dataset.stringSelected || '',
        progress: this.dataset.stringProgress || '[valgt] af [total] valgt',
        emptySlot: this.dataset.stringEmptySlot || 'Vælg et produkt',
        savings: this.dataset.stringSavings || 'Du sparer [beløb]',
        includedTag: this.dataset.stringIncludedTag || 'Medfølger'
      };

      this.selection = [];

      this.includedItems = Array.from(this.querySelectorAll('[data-included-item]')).map((element) => ({
        variantId: parseInt(element.dataset.variantId, 10),
        price: parseInt(element.dataset.price, 10) || 0,
        title: element.dataset.title,
        variantTitle: element.dataset.variantTitle,
        image: element.dataset.image
      }));

      this.includedTotal = this.includedItems.reduce((sum, item) => sum + item.price, 0);

      this.summary = this.querySelector('[data-summary]');
      this.slotList = this.querySelector('[data-bundle-slots]');
      this.miniSlots = this.querySelector('[data-mini-slots]');
      this.savingsElement = this.querySelector('[data-savings]');
      this.addSlotButton = this.querySelector('[data-add-slot]');
      this.submitButton = this.querySelector('[data-submit-bundle]');
      this.errorElement = this.querySelector('[data-bundle-error]');
      this.productGrid = this.querySelector('[data-product-grid]');
      this.cardsSection = this.dataset.cardsSection;

      // The server rendered the open tab only; the rest arrive on first click.
      this.loadedTabs = new Set();
      const initialTab = this.querySelector('[data-tab].is-active');
      if (initialTab) this.loadedTabs.add(initialTab.dataset.tab);

      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
      document.addEventListener('click', this.onDocumentClick);

      this.hydrateCards(this);

      // Only float the mobile bar while the builder itself is on screen.
      this.observer = new IntersectionObserver(
        ([entry]) => this.classList.toggle('is-in-view', entry.isIntersecting),
        { rootMargin: '0px 0px -80px 0px' }
      );
      this.observer.observe(this);

      this.render();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      document.removeEventListener('click', this.onDocumentClick);
    }

    /* ------------------------------------------------------------- EVENTS */

    onDocumentClick = (event) => {
      if (!event.target.closest('[data-variant-picker]')) this.closePickers();
    };

    onClick = (event) => {
      const toggle = event.target.closest('[data-variant-toggle]');
      if (toggle) return this.togglePicker(toggle.closest('[data-variant-picker]'));

      const option = event.target.closest('[data-variant-option]');
      if (option) return this.selectVariant(option.closest('[data-bundle-card]'), option);

      const tab = event.target.closest('[data-tab]');
      if (tab) return this.selectTab(tab);

      const addButton = event.target.closest('[data-add-to-bundle]');
      if (addButton) return this.addToBundle(addButton.closest('[data-bundle-card]'));

      if (event.target.closest('[data-add-slot]')) return this.addSlot();
      if (event.target.closest('[data-remove-extra-slot]')) return this.removeSlot();

      const removeButton = event.target.closest('[data-remove-slot]');
      if (removeButton) return this.removeFromBundle(parseInt(removeButton.dataset.removeSlot, 10));

      const summaryToggle = event.target.closest('[data-toggle-summary]');
      if (summaryToggle) return this.toggleSummary(summaryToggle);

      if (event.target.closest('[data-submit-bundle]')) return this.submit();
    };

    onKeydown = (event) => {
      const picker = event.target.closest('[data-variant-picker]');
      if (!picker) return;

      if (event.key === 'Escape') {
        this.closePickers();
        picker.querySelector('[data-variant-toggle]')?.focus();
        return;
      }

      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;

      event.preventDefault();

      if (!picker.classList.contains('is-open')) return this.openPicker(picker);

      const options = Array.from(picker.querySelectorAll('[data-variant-option]:not(:disabled)'));
      const current = options.indexOf(document.activeElement);
      const next = event.key === 'ArrowDown' ? current + 1 : current - 1;

      options[(next + options.length) % options.length]?.focus();
    };

    /* --------------------------------------------------------------- TABS */

    // Runs on the server-rendered cards and again on every fetched batch.
    hydrateCards(scope) {
      // The cards are real theme product cards, so their image and title link to
      // the PDP. Open those in a new tab instead of dropping the shopper out of a
      // half-finished bundle.
      scope.querySelectorAll('.advent-card a[href]').forEach((link) => {
        link.target = '_blank';
        link.rel = 'noopener';
      });

      // Fetched cards carry the cards section's own label defaults, so restate
      // whatever the parent section is configured with.
      if (this.strings.selected) {
        scope.querySelectorAll('.advent-card__badge').forEach((badge) => {
          badge.textContent = this.strings.selected;
        });
      }

      scope.querySelectorAll('[data-variant-picker]:not(.has-value) [data-variant-label]').forEach((label) => {
        label.textContent = this.strings.chooseSize;
      });

      // Nothing to choose on single variant cards: resolve them up front.
      scope.querySelectorAll('[data-bundle-card][data-single-variant="true"]').forEach((card) => {
        const option = card.querySelector('[data-variant-option][data-available="true"]');
        if (option) this.selectVariant(card, option, { silent: true });
      });
    }

    async selectTab(tab) {
      const value = tab.dataset.tab;

      if (tab.getAttribute('aria-busy') === 'true') return;
      if (!this.loadedTabs.has(value) && !(await this.loadTab(tab))) return;

      this.querySelectorAll('[data-tab]').forEach((button) => {
        const isActive = button === tab;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      this.querySelectorAll('[data-bundle-card]').forEach((card) => {
        card.hidden = card.dataset.collection !== value;
      });
    }

    // Pulls one subcollection's cards through the Section Rendering API and keeps
    // them in the grid, tagged with the tab index, for the rest of the session.
    async loadTab(tab) {
      const value = tab.dataset.tab;
      const url = tab.dataset.url;

      if (!url || !this.cardsSection || !this.productGrid) return false;

      tab.setAttribute('aria-busy', 'true');
      this.setError(null);

      try {
        const separator = url.includes('?') ? '&' : '?';
        const response = await fetch(`${url}${separator}section_id=${this.cardsSection}`);

        if (!response.ok) throw new Error(`Kunne ikke hente produkterne (${response.status}).`);

        const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
        const cards = parsed.querySelectorAll('[data-bundle-card]');

        if (!cards.length) throw new Error('Der er ingen produkter i denne kollektion.');

        const added = [];

        cards.forEach((card) => {
          const imported = document.importNode(card, true);
          imported.dataset.collection = value;
          imported.hidden = true;
          this.productGrid.appendChild(imported);
          added.push(imported);
        });

        this.loadedTabs.add(value);

        // Hydrate only the batch that just landed.
        added.forEach((card) => this.hydrateCards(card));

        this.render();
        return true;
      } catch (error) {
        this.setError(error.message);
        return false;
      } finally {
        tab.removeAttribute('aria-busy');
      }
    }

    /* ------------------------------------------------------- SIZE PICKER */

    openPicker(picker) {
      this.closePickers(picker);

      picker.classList.add('is-open');
      picker.querySelector('[data-variant-toggle]')?.setAttribute('aria-expanded', 'true');

      const panel = picker.querySelector('[data-variant-panel]');
      if (panel) panel.hidden = false;

      const focusTarget =
        picker.querySelector('[data-variant-option][aria-selected="true"]:not(:disabled)') ||
        picker.querySelector('[data-variant-option]:not(:disabled)');

      focusTarget?.focus();
    }

    closePickers(except = null) {
      this.querySelectorAll('[data-variant-picker].is-open').forEach((picker) => {
        if (picker === except) return;

        picker.classList.remove('is-open');
        picker.querySelector('[data-variant-toggle]')?.setAttribute('aria-expanded', 'false');

        const panel = picker.querySelector('[data-variant-panel]');
        if (panel) panel.hidden = true;
      });
    }

    togglePicker(picker) {
      if (!picker) return;

      if (picker.classList.contains('is-open')) {
        this.closePickers();
        picker.querySelector('[data-variant-toggle]')?.focus();
      } else {
        this.openPicker(picker);
      }
    }

    selectVariant(card, option, { silent = false } = {}) {
      if (!card || option.disabled) return;

      const picker = card.querySelector('[data-variant-picker]');

      picker?.querySelectorAll('[data-variant-option]').forEach((button) => {
        button.setAttribute('aria-selected', button === option ? 'true' : 'false');
      });

      picker?.classList.add('has-value');

      const label = picker?.querySelector('[data-variant-label]');
      if (label) label.textContent = option.dataset.variantTitle;

      card.dataset.selectedVariant = option.dataset.variantId;

      if (silent) return;

      this.closePickers();
      picker?.querySelector('[data-variant-toggle]')?.focus();
      this.render();
    }

    clearSelection(card) {
      const picker = card.querySelector('[data-variant-picker]');

      picker?.querySelectorAll('[data-variant-option]').forEach((button) => {
        button.setAttribute('aria-selected', 'false');
      });

      picker?.classList.remove('has-value');

      const label = picker?.querySelector('[data-variant-label]');
      if (label) label.textContent = this.strings.chooseSize;

      delete card.dataset.selectedVariant;
    }

    /* --------------------------------------------------------- SELECTION */

    itemFrom(card, option) {
      return {
        variantId: parseInt(option.dataset.variantId, 10),
        productId: card.dataset.productId,
        title: card.dataset.productTitle,
        variantTitle: card.dataset.singleVariant === 'true' ? '' : option.dataset.variantTitle,
        price: parseInt(option.dataset.variantPrice, 10) || 0,
        image: option.dataset.variantImage || card.dataset.productImage
      };
    }

    addToBundle(card) {
      if (!card || this.selection.length >= this.slotCount) return;

      const option = card.querySelector(`[data-variant-option][data-variant-id="${card.dataset.selectedVariant}"]`);
      if (!option || option.disabled) return;

      this.selection.push(this.itemFrom(card, option));
      this.setError(null);
      this.render();
    }

    removeFromBundle(index) {
      this.selection.splice(index, 1);
      this.render();
    }

    addSlot() {
      if (this.slotCount >= this.bundleMax) return;

      this.slotCount += 1;
      this.render();
    }

    // Slots past the starting count were opened by the shopper, so they can close
    // them again — otherwise one empty extra slot blocks the CTA for good.
    removeSlot() {
      if (this.slotCount <= this.bundleSize || this.selection.length >= this.slotCount) return;

      this.slotCount -= 1;
      this.render();
    }

    toggleSummary(toggle) {
      const isOpen = this.summary.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    }

    /* ------------------------------------------------------------ PRICES */

    // Shopify rounds a percentage discount per line, so mirror that here or the
    // preview drifts from the cart by a few øre.
    discountedPrice(price) {
      return price - Math.round((price * this.discount) / 100);
    }

    get usedVariantIds() {
      return new Set(this.selection.map((item) => item.variantId));
    }

    get itemsTotal() {
      return this.selection.reduce((sum, item) => sum + this.discountedPrice(item.price), 0);
    }

    get savings() {
      return this.selection.reduce((sum, item) => sum + (item.price - this.discountedPrice(item.price)), 0);
    }

    get total() {
      return this.includedTotal + this.itemsTotal;
    }

    get isComplete() {
      return this.selection.length === this.slotCount;
    }

    /* ------------------------------------------------------------ RENDER */

    render() {
      this.renderSlots();
      this.renderMiniSlots();
      this.renderCards();
      this.renderTotals();
    }

    renderSlots() {
      if (!this.slotList) return;

      const badge = this.discount > 0 ? `<span class="advent-slot__discount">-${this.discount}%</span>` : '';

      const slots = Array.from({ length: this.slotCount }, (_, index) => {
        const item = this.selection[index];

        if (!item) {
          const removable =
            index >= this.bundleSize
              ? `<button type="button" class="advent-slot__remove" data-remove-extra-slot aria-label="Fjern plads"><span aria-hidden="true">&times;</span></button>`
              : '';

          return `<li class="advent-slot">
            <span class="advent-slot__thumb" aria-hidden="true"></span>
            <span class="advent-slot__placeholder">${escapeHtml(this.strings.emptySlot)}</span>
            ${badge}
            ${removable}
          </li>`;
        }

        const meta = [item.variantTitle, formatMoney(item.price)].filter(Boolean).join(' · ');

        return `<li class="advent-slot is-filled">
          <img class="advent-slot__image" src="${escapeHtml(item.image)}" alt="" width="40" height="53" loading="lazy">
          <span class="advent-slot__info">
            <span class="advent-slot__title">${escapeHtml(item.title)}</span>
            <span class="advent-slot__meta">
              <s>${escapeHtml(meta)}</s>
              <strong>${formatMoney(this.discountedPrice(item.price))}</strong>
            </span>
          </span>
          ${badge}
          <button type="button" class="advent-slot__remove" data-remove-slot="${index}" aria-label="Fjern ${escapeHtml(item.title)}">
            <span aria-hidden="true">&times;</span>
          </button>
        </li>`;
      });

      // Included products get their own line rather than a footnote, so the
      // shopper sees exactly what the starting price covers.
      const included = this.includedItems.map((item) => {
        const meta = [item.variantTitle, formatMoney(item.price)].filter(Boolean).join(' · ');

        return `<li class="advent-slot is-filled advent-slot--included">
          <img class="advent-slot__image" src="${escapeHtml(item.image)}" alt="" width="40" height="53" loading="lazy">
          <span class="advent-slot__info">
            <span class="advent-slot__title">${escapeHtml(item.title)}</span>
            <span class="advent-slot__meta">${escapeHtml(meta)}</span>
          </span>
          <span class="advent-slot__tag">${escapeHtml(this.strings.includedTag)}</span>
        </li>`;
      });

      this.slotList.innerHTML = [...slots, ...included].join('');
    }

    // Collapsed mobile bar: thumbnails of what is in the bundle so far, plus the
    // included products, so the shopper sees the whole package without expanding.
    renderMiniSlots() {
      if (!this.miniSlots) return;

      const slots = Array.from({ length: this.slotCount }, (_, index) => {
        const item = this.selection[index];

        return item
          ? `<span class="advent-mini is-filled"><img src="${escapeHtml(item.image)}" alt="" width="32" height="42" loading="lazy"></span>`
          : '<span class="advent-mini"></span>';
      });

      const included = this.includedItems.map(
        (item) =>
          `<span class="advent-mini is-filled advent-mini--included"><img src="${escapeHtml(item.image)}" alt="" width="32" height="42" loading="lazy"></span>`
      );

      this.miniSlots.innerHTML = [...slots, ...included].join('');
    }

    renderCards() {
      const used = this.usedVariantIds;
      const isFull = this.isComplete;

      this.querySelectorAll('[data-bundle-card]').forEach((card) => {
        const isSingle = card.dataset.singleVariant === 'true';
        let selectedId = parseInt(card.dataset.selectedVariant, 10);
        let isInBundle = !this.allowDuplicates && used.has(selectedId);

        card.querySelectorAll('[data-variant-option]').forEach((option) => {
          const id = parseInt(option.dataset.variantId, 10);
          const isUsedElsewhere = !this.allowDuplicates && used.has(id) && id !== selectedId;

          option.disabled = option.dataset.available !== 'true' || isUsedElsewhere;
        });

        // The same product can appear under two subcollection tabs. If the other
        // card grabbed this variant, drop the stale selection here.
        if (!isSingle && card.dataset.selectedVariant) {
          const selectedOption = card.querySelector(
            `[data-variant-option][data-variant-id="${card.dataset.selectedVariant}"]`
          );

          if (!selectedOption || selectedOption.disabled) {
            this.clearSelection(card);
            selectedId = NaN;
            isInBundle = false;
          }
        }

        card.classList.toggle('is-selected', Boolean(isInBundle));

        const addButton = card.querySelector('[data-add-to-bundle]');
        if (!addButton) return;

        if (isInBundle) {
          addButton.disabled = true;
          addButton.textContent = this.strings.added;
        } else if (isFull) {
          addButton.disabled = true;
          addButton.textContent = this.strings.full;
        } else if (!card.dataset.selectedVariant) {
          addButton.disabled = true;
          addButton.textContent = this.strings.chooseSize;
        } else {
          addButton.disabled = false;
          addButton.textContent = this.strings.add;
        }
      });
    }

    renderTotals() {
      const remaining = this.slotCount - this.selection.length;
      const counter = this.strings.progress
        .replace('[valgt]', this.selection.length)
        .replace('[total]', this.slotCount);

      this.querySelectorAll('[data-summary-count], [data-summary-count-desktop]').forEach((element) => {
        element.textContent = counter;
      });

      this.querySelectorAll('[data-summary-total], [data-summary-total-mobile]').forEach((element) => {
        element.textContent = formatMoney(this.total);
      });

      if (this.savingsElement) {
        this.savingsElement.hidden = this.savings === 0;
        this.savingsElement.textContent = this.strings.savings.replace('[beløb]', formatMoney(this.savings));
      }

      if (this.addSlotButton) {
        this.addSlotButton.hidden = this.slotCount >= this.bundleMax;
      }

      if (this.submitButton) {
        if (remaining > 0) {
          this.submitButton.disabled = true;
          this.submitButton.textContent = this.strings.remaining.replace('[mangler]', remaining);
        } else {
          this.submitButton.disabled = false;
          this.submitButton.textContent = this.strings.cta;
        }
      }
    }

    setError(message) {
      if (!this.errorElement) return;

      this.errorElement.textContent = message || '';
      this.errorElement.hidden = !message;
    }

    /* ------------------------------------------------------------ SUBMIT */

    async submit() {
      if (!this.isComplete || this.submitButton.hasAttribute('aria-busy')) return;

      const bundleId = `${this.dataset.sectionId}-${Date.now()}`;
      const baseProperties = { _bundle: this.bundleName, _bundle_id: bundleId };

      // A property without a leading underscore is the only one Shopify shows in
      // the cart, at checkout and on the order / packing slip.
      if (this.dataset.linePropertyName) {
        baseProperties[this.dataset.linePropertyName] = this.dataset.linePropertyValue || this.bundleName;
      }

      const items = [
        ...this.selection.map((item) => ({
          id: item.variantId,
          quantity: 1,
          properties: { ...baseProperties }
        })),
        ...this.includedItems.map((item) => ({
          id: item.variantId,
          quantity: 1,
          properties: { ...baseProperties, _inkluderet: 'true' }
        }))
      ];

      // Ask the theme which sections need re-rendering (cart drawer, cart count…)
      const sectionsToBundle = new Set();
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
      );

      this.submitButton.setAttribute('aria-busy', 'true');
      this.setError(null);

      try {
        const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify({ items, sections: [...sectionsToBundle].join(',') })
        });

        const responseJson = await response.json();

        if (!response.ok) {
          throw new Error(responseJson.description || responseJson.message);
        }

        if (window.themeVariables.settings.cartType === 'page' || window.themeVariables.settings.pageType === 'cart') {
          window.location.href = `${Shopify.routes.root}cart`;
          return;
        }

        const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
        cartContent.sections = responseJson.sections;

        document.documentElement.dispatchEvent(
          new CustomEvent('cart:change', { bubbles: true, detail: { baseEvent: 'variant:add', cart: cartContent } })
        );

        this.reset();
      } catch (error) {
        this.setError(error.message);
        document.dispatchEvent(new CustomEvent('toast:show', { detail: { message: error.message, tone: 'error' } }));
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      } finally {
        this.submitButton.removeAttribute('aria-busy');
      }
    }

    reset() {
      this.selection = [];
      this.slotCount = this.bundleSize;
      this.summary?.classList.remove('is-open');
      this.querySelector('[data-toggle-summary]')?.setAttribute('aria-expanded', 'false');
      this.closePickers();

      this.querySelectorAll('[data-bundle-card]:not([data-single-variant="true"])').forEach((card) => {
        this.clearSelection(card);
      });

      this.render();
    }
  }

  if (!window.customElements.get('advent-bundle')) {
    window.customElements.define('advent-bundle', AdventBundle);
  }
})();